import generateScriptElement from '@capsule/generate-script-element'
import PmcBridge from '@capsule/pmc-bridge'
import type { BatchApi, Config } from './types.js'

/**
 * Batch SDK
 * @module BatchSdk
 */
export default class BatchSdk {
	config: Config
	onReady: (api: BatchApi) => void
	pmcBridge: PmcBridge

	/**
	 * Constructor
	 * @param options Options
	 * @param options.config Config options for Batch SDK
	 * @param options.onReady On ready callback function
	 */
	constructor({
		config,
		onReady = () => {}
	}: {
		config: Config
		onReady: (api: BatchApi) => void
	}) {
		this.config = config
		this.onReady = onReady

		this.pmcBridge = new PmcBridge()

		this.onBatchLoaded = this.onBatchLoaded.bind(this)
		this.onBatchApi = this.onBatchApi.bind(this)
		this.onSubscriptionChanged = this.onSubscriptionChanged.bind(this)
	}

	/**
	 * Load the SDK
	 */
	init() {
		this.declareQueue()
		const script = generateScriptElement({
			src: 'https://via.batch.com/v4/bootstrap.min.js',
			onLoadCallback: this.onBatchLoaded
		})
		document.body.appendChild(script)
	}

	/**
	 * Declare SDK queue
	 */
	declareQueue() {
		window.batchSDK =
			window.batchSDK ||
			((...args) => {
				// biome-ignore lint : custom
				;(window.batchSDK.q = window.batchSDK.q || []).push(args)
			})
	}

	/**
	 * When the SDK is loaded
	 */
	onBatchLoaded() {
		window.batchSDK('setup', this.config.setup)
		window.batchSDK(this.onBatchApi)
	}

	/**
	 * When The SDK API is ready
	 * @param api Batch SDK API
	 */
	async onBatchApi(api: BatchApi) {
		api.on('subscriptionChanged', this.onSubscriptionChanged)
		this.onReady(api)
		this.setCustomUserId(api)
	}

	/**
	 * On subscription changed event
	 * @param api Batch SDK API
	 * @param subscription Batch SDK subscription
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	async onSubscriptionChanged(api: BatchApi, subscription: any) {
		if (
			subscription.subscribed &&
			window.localStorage.getItem('batchSubscriptionChanged') !== 'true'
		) {
			window.localStorage.setItem('batchSubscriptionChanged', 'true')
			const registration = await window.navigator.serviceWorker.ready
			registration.showNotification(
				this.config.showNotification.title,
				this.config.showNotification.options ?? {}
			)
		}
	}

	/**
	 * Set custom user ID, from PMC or default value
	 * @param api Batch SDK API
	 */
	async setCustomUserId(api: BatchApi) {
		const pmcProfile = await this.pmcBridge.getProfile()
		const batchProfile = await api.profile()

		if (pmcProfile) {
			await batchProfile.identify({ customId: pmcProfile.id })
			window.dataLayer.push({ event: 'dispatch', batch_user_id: pmcProfile.id })
		} else {
			const id = await api.getInstallationID()
			await batchProfile.identify({ customId: id })
			window.dataLayer.push({ event: 'dispatch', batch_user_id: id })
		}
	}
}
